import {LitElement, css, html, PropertyValues} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { ParameterType, PerfDataSetName, VesselPerformanceModel } from '../../../src/models/vessel-performance-model';
import { PerformanceUtils } from '../../../src/utils/performance-utils';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { extractAllTimestamps, getHardWaypointsInfoFromRoutes, getPilotPointIndeces, getPilotPointsTimestamps, getPilotPointWaypointNumbers, getWaypointDisplayStartIndex } from '../../../src/utils/route-utils';
import { extractSpotWeatherDataFromRoute } from '../../../src/utils/utils';
import { TooltipType } from '../../voy-timeseries-chart/src/voy-timeseries-chart-tooltip';
import { ChartNames, echartUtils, PilotPointTimestamps, RouteSeriesNames, TimeseriesData, TimeseriesValues, TimestampData, WaypointTimestamps } from '../../voy-timeseries-chart/src/echartUtils';
import { VoyTimeseriesChart } from '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { repeat } from 'lit-html/directives/repeat';
import { ChartMode, ChartsVisibility } from './chart-selection';
import { debounce, GetFormattedLocalDate } from '../../../src/utils/helpers';
import './route-selection';
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import '../../voyage-spinner/src/voyage-spinner';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';

// Echarts are currently loaded as global
declare const echarts: any;

const performanceTypes = [ChartNames.Sog, ChartNames.Stw, ChartNames.CurrentImpact, ChartNames.Power, ChartNames.Rpm, ChartNames.RollAngle];
const weatherTypes = [ChartNames.Wind50M, ChartNames.Currents, ChartNames.Wave, ChartNames.Swell];
const chartTypes: ChartNames[] = [...performanceTypes, ...weatherTypes];

export class VesselPerformanceFullVoyagePlanning extends LitElement {

    @property({type: Number}) currentTime: number = Date.now();
    @property({type: Object}) plan: VoyagePlan|null = null;
    @property({type: Boolean}) maximized: boolean = false;

    constructor()
    {
        super();

        this._updatePerformanceData = this._updatePerformanceData.bind(this);
        this._updateFirstShownChart = this._updateFirstShownChart.bind(this);
        this._updateData = this._updateData.bind(this);
        this._debouncedUpdateData = debounce(this._updateData, 200);
        this._extractAllTimestamps = this._extractAllTimestamps.bind(this);
        this._debouncedExtractAllTimestamps = debounce(this._extractAllTimestamps, 200)
        this._onRouteOptionsUpdated = this._onRouteOptionsUpdated.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: column;
                max-height: calc(100vh - 230px);
                width: 100%;
                overflow-y: scroll;
                pointer-events: auto;
                box-shadow: 1px 1px 5px var(--secondary-voyage-box-shadow);
            }

            .container {
                display: none;
                background: var(--neutrals-grey-10, white);
            }

            .container[shown] {
                display: block;
                background: var(--neutrals-grey-10, white);
            }

            .route-selection-container{
                background: var(--neutrals-grey-10, white);
            }

            .divider {
                width: calc(100% - 32px);
                height: 1px;
                background-color: var(--neutrals-grey-06, black);
                border: none;
            }

            .route-info-container {
                background: var(--neutrals-grey-10, white);
                padding: 12px 16px;
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .route-info-item {
                font-size: 12px;
                color: var(--neutrals-grey-03);
            }

            .route-info-label {
                font-weight: 600;
                margin-right: 8px;
            }

            .route-info-detail {
                color: var(--neutrals-grey-04);
            }

            .route-info-loading {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                color: var(--neutrals-grey-04);
            }

            .route-info-spinner {
                display: inline-block;
                width: 12px;
                height: 12px;
            }

            .list {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
            }

            .chart {
                display: none;
                flex-direction: row;
                font-family: 'MaerskText';
            }

            .chart[shown] {
                display: flex;
            }

            .title {
                color: var(--neutrals-grey-03, black);
                font-size: 14px;
                margin: 10px 10px 0px 16px;
                display: flex;
            }

            voy-timeseries-chart {
                pointer-events: auto;
            }

            .question-mark {
                cursor: pointer;
                margin-left: 10px;
            }
        `];
    }

    render() {

        const maxRollAngleValue = Math.max(...this._chartsData['rollAngle'].flatMap(data => 
            data.timeseries.values.filter((v): v is number => v !== null)
        ));

        const maxRollAngleYAxis = this._rollAngleThresholdLimit && this._rollAngleThresholdLimit > maxRollAngleValue 
            ? this._rollAngleThresholdLimit : maxRollAngleValue;

        return html`
            <chart-selection
                @chart-options-updated=${this._onChartOptionsUpdated}
                @chart-selection-loaded=${this._onChartSelectionLoaded}
                .chartsKey=${ChartMode.PlanningMode}
                .maximized=${this.maximized}
            >
            </chart-selection>

            <div class="route-selection-container">
                <hr class="divider">
                <route-selection
                .routes=${this.plan ? this.plan.routes : []}
                @chart-route-selection-updated=${this._onRouteOptionsUpdated}
                .maximized=${this.maximized}
                >
                </route-selection>
            </div>

            ${this.plan && this.plan.routes.length > 0 ? html`
                <div class="route-info-container">
                    <hr class="divider">
                    ${this.plan.routes.map((route, i) => {
                        // Show a loading spinner while a route is (re)calculating
                        if (route.recalcNeeded) {
                            return html`
                                <div class="route-info-item">
                                    <span class="route-info-label">Route ${i + 1}:</span>
                                    <span class="route-info-loading">
                                        <voyage-spinner class="route-info-spinner"></voyage-spinner>
                                        Calculating…
                                    </span>
                                </div>
                            `;
                        }

                        const forecastVersionRaw = route.route_info?.calculationInput?.forecast_versions?.[0] || '';
                        // Format forecast version: F20260812060000 -> 2026-08-12 06:00
                        let forecastVersion = 'N/A';
                        if (forecastVersionRaw && forecastVersionRaw.startsWith('F') && forecastVersionRaw.length === 15) {
                            const dateStr = forecastVersionRaw.substring(1); // Remove 'F'
                            if (dateStr === '00000000000000') {
                                forecastVersion = 'Climatic';
                            } else {
                                const year = dateStr.substring(0, 4);
                                const month = dateStr.substring(4, 6);
                                const day = dateStr.substring(6, 8);
                                const hour = dateStr.substring(8, 10);
                                const minute = dateStr.substring(10, 12);
                                forecastVersion = `${year}-${month}-${day} ${hour}:${minute}`;
                            }
                        }
                        
                        const timestamp = route.creationTime;
                        const formattedDate = timestamp ? GetFormattedLocalDate(timestamp) : 'N/A';

                        return html`
                            <div class="route-info-item">
                                <span class="route-info-label">Route ${i + 1}:</span>
                                <span class="route-info-detail">
                                    Forecast: ${forecastVersion} | Calculated: ${formattedDate}
                                </span>
                            </div>
                        `;
                    })}
                </div>
            ` : ''}

            <div class="list">
                ${repeat(chartTypes, (chartType) => {
                    const shown = this._chartsVisibility[(<keyof ChartsVisibility>chartType)];
                    const isPerformance = performanceTypes.includes(chartType);

                    return html`
                        <div class="container" ?shown=${shown}>
                            <hr class="divider">
                            <div class="title">
                                <span>${echartUtils.getChartTitle(chartType)}</span>
                                ${!isPerformance
                                    ? html`
                                        <div class="question-mark">
                                            ${SvgIcons.lightQuestionCircle}
                                            <voyage-tooltip-proxy
                                                text="Historical forecast is not available at this time.<br>Developers are working on delivering this."
                                                html-content
                                                .positionPriority=${["top"]}>
                                            </voyage-tooltip-proxy>
                                        </div>`
                                    : html``
                                }
                            </div>
                            <div class="chart" ?shown=${shown}>
                                <voy-timeseries-chart
                                    id=${`${chartType}chart`}
                                    unit=${echartUtils.getUnit(chartType)}
                                    .data=${shown ? this._chartsData[chartType] : []}
                                    .allTimestamps=${shown ? this._allTimestamps : []}
                                    .pilotPoints=${this._pilotPoints}
                                    .tooltipType=${isPerformance ? TooltipType.standart : TooltipType.weather}
                                    .routesAndWaypoints=${this._hardWaypoints}
                                    .pilotPointWaypointNumbers=${this._pilotPointWaypointNumbers}
                                    .showWaypoints=${this._firstShownChart === chartType}
                                    .showLegend=${true}
                                    .hiddenSeries=${this._hiddenSeries}
                                    .yAxisChartParams=${{
                                                        nameRotate: 0,
                                                        nameLocation: 'middle',
                                                        padding: [0,30,0,0],
                                                        nameGap: 10,
                                                         ...(chartType === "rollAngle" && { max: maxRollAngleYAxis * 1.3 })
                                                    }}
                                >
                                </voy-timeseries-chart>
                            </div>
                        </div>
                        
                    `
                })}
            </div>
        `;
    }

firstUpdated()
{
    VesselPerformanceModel!.addEventListener('performance-updated', this._updatePerformanceData);
    VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.RoutesChanged, this._debouncedUpdateData);

    this._updatePerformanceData();
    this._debouncedUpdateData();

    // Wait for the next frame to ensure all charts are rendered
    requestAnimationFrame(() => {
        this.updateComplete.then(() => {
            const sogchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#sogchart');
            const stwchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#stwchart');
            const currentImpactChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#currentImpactchart');
            const powerchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#powerchart');
            const rpmchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#rpmchart');
            const wind50Mchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#wind50Mchart');
            const wavechart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#wavechart');
            const swellchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#swellchart');
            const currentschart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#currentschart');
            const rollAngleChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#rollAnglechart');

            const charts = [
                sogchart, 
                stwchart, 
                currentImpactChart, 
                powerchart, 
                rpmchart, 
                wind50Mchart, 
                wavechart, 
                swellchart, 
                currentschart,
                rollAngleChart
            ].filter(c => c !== null);

            VoyTimeseriesChart.connect(charts);
        });
    });
}

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((_, propName) => {
            if (propName === "plan") {
                this._debouncedUpdateData();
            }
        });
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        VesselPerformanceModel!.removeEventListener('performance-updated', this._updatePerformanceData);
        VoyageRoutingModel!.removeEventListener(SuggestedRouteEvents.RoutesChanged, this._debouncedUpdateData);
    }

    @property({attribute: false}) private _pilotPoints: PilotPointTimestamps = {departure: -1, arrival: -1};
    @property({attribute: false}) private _hardWaypoints: WaypointTimestamps[][] = [];
    @property({attribute: false}) private _pilotPointWaypointNumbers: number[][] = [];
    @property({attribute: false}) private _allTimestamps: TimestampData[] = [];
    @property({attribute: false}) private _firstShownChart: keyof ChartsVisibility = ChartNames.Sog;
    @property({attribute: false}) private _hiddenSeries: RouteSeriesNames[] = [];
    @property({attribute: false}) private _ecdisWPStartIndex: number|null = null;
    @property({attribute: false}) private _rollAngleThresholdLimit: number | null = null; 


    private _debouncedUpdateData: () => void;
    private _debouncedExtractAllTimestamps: () => void;
    @property({attribute: false}) private _chartsData: {[index in ChartNames]: TimeseriesData[]} = {
        sog: [],
        power: [],
        rpm: [],
        wind50M: [],
        currents: [],
        wave: [],
        swell: [],
        stw: [],
        currentImpact: [],
        rollAngle: []
    }
    @property({attribute: false}) private _chartsVisibility: ChartsVisibility = {
        sog: true,
        power: false,
        rpm: false,
        wind50M: false,
        currents: false,
        wave: false,
        swell: false,
        stw: false,
        currentImpact: false,
        rollAngle: false
    }

    private _extractAllTimestamps() {
        this._allTimestamps = extractAllTimestamps(this._pilotPoints, this._chartsData, this._hardWaypoints);
    }

    private _onChartSelectionLoaded(e: CustomEvent) {
        const {options} = e.detail
        this._chartsVisibility = options;
        this._updateFirstShownChart();
    }

    private _onChartOptionsUpdated(e: CustomEvent) {
        const {keyChanged, newValue} = e.detail.state
        this._chartsVisibility[<keyof ChartsVisibility>keyChanged] = newValue;
        this._updateFirstShownChart();
        this.requestUpdate();
    }

    private _onRouteOptionsUpdated(e: CustomEvent) {
        const {options, changedValue, newValue} = e.detail;
        const hiddenRoutes: RouteSeriesNames[] = [];
        
        for (const key of Object.keys(options)) {
            if (options[key] === false) {
                hiddenRoutes.push(<RouteSeriesNames>key);
            }
        }
        this._hiddenSeries = hiddenRoutes;

        VoyTimeseriesChart.updateSeriesVisibility([changedValue], newValue);
    }

    private _updateFirstShownChart() {
        for (const key of Object.keys(this._chartsVisibility)) {
            if (this._chartsVisibility[<keyof ChartsVisibility>key]) {
                this._firstShownChart = <keyof ChartsVisibility>key;
                return;
            }
        }
    }

    private async _updateData() {
        if (!this.plan) return;


        if (!this.plan.routes.length) return;
        this._pilotPoints = getPilotPointsTimestamps(this.plan);
        this._ecdisWPStartIndex = await this._getVesselCharacteristics();
        const waypointNumberingOptions = { applyRouteEditorWaypointFilter: true };
        this._hardWaypoints = getHardWaypointsInfoFromRoutes(this.plan.routes, this._ecdisWPStartIndex, waypointNumberingOptions);
        this._pilotPointWaypointNumbers = getPilotPointWaypointNumbers(this.plan.routes, this._ecdisWPStartIndex, waypointNumberingOptions);
        const weatherData = extractSpotWeatherDataFromRoute(this.plan.routes, "planning")
        this._chartsData.wind50M = weatherData.wind50M;
        this._chartsData.swell = weatherData.swell;
        this._chartsData.wave = weatherData.wave;
        this._chartsData.currents = weatherData.currents;
        this._debouncedExtractAllTimestamps();
    }

    private async _getVesselCharacteristics() {
        const vesselCharacteristics =  await VesselPlanningModel!.getVesselCharacteristics();
        return getWaypointDisplayStartIndex(vesselCharacteristics?.ecdisWPStartIndex);
    }

    private _updatePerformanceData() {
        const KmToNM = 1 / 1.852; // Km -> Nautical miles

        try {
            const sogs: TimeseriesData[] = [];
            const powers: TimeseriesData[] = [];
            const rpms: TimeseriesData[] = [];
            const stws: TimeseriesData[] = [];
            const currentImpacts: TimeseriesData[] = [];
            const rollAngles: TimeseriesData[] = [];


            for (let i = 1; i <= 5; i++)
            {
                const routeName = `route${i}`;
                const color = `--route${i}-color`;

                const sog = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.SpeedOverGround, true);
                if (sog.length > 0)
                {
                    sogs.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: PerformanceUtils.paramValuesToTimeseries(sog) });
                }

                const power = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.PropulsionPower, true);
                if (power.length > 0)
                {
                    const timeseries: TimeseriesValues = PerformanceUtils.paramValuesToTimeseries(power);
                    // round values to whole numbers
                    timeseries.values = timeseries.values.map(number => Math.round(number!));
                    if (timeseries.lower)
                    {
                        timeseries.lower = timeseries.lower.map(number => Math.round(number!));
                    }
                    if (timeseries.upper)
                    {
                        timeseries.upper = timeseries.upper.map(number => Math.round(number!));
                    }
                    
                    powers.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: timeseries });
                }

                const rpm = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.RPM, true);
                if (rpm.length > 0)
                {
                    rpms.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: PerformanceUtils.paramValuesToTimeseries(rpm) });
                }

                const stw = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.SpeedThroughWater, true);
                if (stw.length > 0)
                {
                    stws.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: PerformanceUtils.paramValuesToTimeseries(stw) });
                }

                const currentImpact = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.CurrentImpact, false);
                if (currentImpact.length > 0)
                {
                    currentImpacts.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: PerformanceUtils.paramValuesToTimeseries(currentImpact) });
                }

                const rollAngle = VesselPerformanceModel!.extractOneParam(<PerfDataSetName>routeName, ParameterType.RollAngle, true);
                if (rollAngle.length > 0)
                {   
                    rollAngles.push({ name: <RouteSeriesNames>`Route ${i}`, color: color, timeseries: PerformanceUtils.paramValuesToTimeseries(rollAngle) });

                    if (this._rollAngleThresholdLimit !== null)
                        {
                            rollAngles.push({
                                name: "Threshold limit",
                                color: "--performance-viewer-threshold-color",
                                timeseries: PerformanceUtils.addConstantToTimeseries(this._allTimestamps, this._rollAngleThresholdLimit)
                            });
                        }
                }

            }

            this._chartsData.sog = sogs
            this._chartsData.power = powers;
            this._chartsData.rpm = rpms;
            this._chartsData.stw = stws;
            this._chartsData.currentImpact = currentImpacts;
            this._chartsData.rollAngle = rollAngles;

            this._debouncedExtractAllTimestamps();
        } catch (err) {
            console.warn(err);
        }
    }
};

customElements.define('vessel-performance-full-voyage-planning', VesselPerformanceFullVoyagePlanning);
